import { Howl, Howler } from 'howler';

/**
 * Audio configuration for the application
 * @type {Object}
 */
const AUDIO_CONFIG = {
  ambientTracks: [
    {
      name: 'ambient1',
      url: 'https://cdn.pixabay.com/audio/2025/02/19/audio_aed6ff9510.mp3',
      fallbackUrl: '/assets/audio/ambient1.mp3',
      volume: 0.3,
      loop: true
    },
    {
      name: 'ambient2',
      url: 'https://cdn.pixabay.com/audio/2025/02/19/audio_aed6ff9510.mp3',
      fallbackUrl: '/assets/audio/ambient2.mp3',
      volume: 0.3,
      loop: true
    }
  ],
  effects: {
    click: {
      url: 'https://cdn.pixabay.com/audio/2021/08/04/audio_12b0c7443c.mp3',
      fallbackUrl: '/assets/audio/click.mp3',
      volume: 0.5
    },
    notification: {
      url: 'https://cdn.pixabay.com/audio/2025/05/01/audio_f41477f1af.mp3',
      fallbackUrl: '/assets/audio/notification.mp3',
      volume: 0.5
    }
  }
};

/**
 * AudioManager class for handling all audio operations
 * Implements singleton pattern to ensure only one instance exists
 */
class AudioManager {
  /**
   * Create an AudioManager instance
   */
  constructor() {
    if (AudioManager.instance) {
      return AudioManager.instance;
    }
    AudioManager.instance = this;
    
    // Initialize state
    this.sounds = {
      ambient: null,
      effects: {}
    };
    
    this.ambientTracks = [];
    this.currentTrackIndex = 0;
    this.isInitialized = false;
    this.isMuted = false;
    this.isPlaying = false;
    this.audioContext = null;
    this.isUnlocked = false;
    this.wasPlayingBeforeMute = false;
    
    // Bind methods
    this.init = this.init.bind(this);
    this.loadSoundEffect = this.loadSoundEffect.bind(this);
    this.loadAmbientTrack = this.loadAmbientTrack.bind(this);
    this.playAmbient = this.playAmbient.bind(this);
    this.stopAmbient = this.stopAmbient.bind(this);
    this.toggleAmbient = this.toggleAmbient.bind(this);
    this.playEffect = this.playEffect.bind(this);
    this.playSound = this.playSound.bind(this);
    this.mute = this.mute.bind(this);
    this.unmute = this.unmute.bind(this);
    this.toggleMute = this.toggleMute.bind(this);
    this.setupAudioUnlock = this.setupAudioUnlock.bind(this);
    this.unlockAudio = this.unlockAudio.bind(this);
    this.updateSoundToggleUI = this.updateSoundToggleUI.bind(this);
    this.createSilentFallback = this.createSilentFallback.bind(this);
  }

  /**
   * Initialize the audio system
   * @returns {Promise<boolean>} True if initialization was successful
   */
  async init() {
    try {
      // Don't initialize multiple times
      if (this.isInitialized) {
        console.log('Audio system already initialized');
        return true;
      }
      
      // Check if browser supports Web Audio API
      if (!window.AudioContext && !window.webkitAudioContext) {
        console.warn('Web Audio API not supported in this browser');
        return false;
      }
      
      // Try to create audio context
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        this.audioContext = new AudioContext();
      } catch (error) {
        console.error('Error creating AudioContext:', error);
        return false;
      }
      
      // Load ambient tracks
      this.ambientTracks = [];
      for (let i = 0; i < AUDIO_CONFIG.ambientTracks.length; i++) {
        await this.loadAmbientTrack(i);
      }
      
      // Load sound effects
      for (const [name, config] of Object.entries(AUDIO_CONFIG.effects)) {
        await this.loadSoundEffect(name, config);
      }
      
      // Unlock audio on first user interaction
      this.setupAudioUnlock();
      
      this.isInitialized = true;
      console.log('Audio system initialized successfully');
    } catch (error) {
      console.error('Error initializing audio system:', error);
    }
  }

  /**
   * Load a sound effect
   * @private
   * @param {string} name - Name of the effect
   * @param {Object} config - Effect configuration
   * @returns {Promise<Object>} The loaded sound
   */
  async loadSoundEffect(name, config) {
    return new Promise((resolve) => {
      try {
        const sound = new Howl({
          src: [config.url, config.fallbackUrl],
          volume: config.volume || 1.0,
          onload: () => {
            console.log(`Loaded sound effect: ${name}`);
            if (!this.sounds.effects) {
              this.sounds.effects = {};
            }
            this.sounds.effects[name] = sound;
            resolve(sound);
          },
          onloaderror: (id, error) => {
            console.error(`Error loading sound effect ${name}:`, error);
            // Create silent fallback
            if (!this.sounds.effects) {
              this.sounds.effects = {};
            }
            this.sounds.effects[name] = this.createSilentFallback('effect');
            resolve(this.sounds.effects[name]);
          }
        });
      } catch (error) {
        console.error('Error in loadSoundEffect:', error);
        // Return silent fallback on error
        if (!this.sounds.effects) {
          this.sounds.effects = {};
        }
        this.sounds.effects[name] = this.createSilentFallback('effect');
        resolve(this.sounds.effects[name]);
      }
    });
  }

  /**
   * Load a specific ambient track by index
   * @param {number} index - Index of the track to load
   * @returns {Promise<Object>} The loaded sound object
   */
  async loadAmbientTrack(index) {
    return new Promise((resolve) => {
      const track = AUDIO_CONFIG.ambientTracks[index];
      if (!track) {
        console.error(`No track found at index ${index}`);
        resolve(this.createSilentFallback('ambient'));
        return;
      }
      
      const sound = new Howl({
        src: [track.url, track.fallbackUrl],
        volume: track.volume,
        loop: track.loop,
        onload: () => {
          console.log(`Loaded ambient track: ${track.name}`);
          this.ambientTracks[index] = sound;
          resolve(sound);
        },
        onloaderror: (id, error) => {
          console.error(`Error loading ambient track ${track.name}:`, error);
          // Fallback to silent sound
          const silentSound = this.createSilentFallback('ambient');
          this.ambientTracks[index] = silentSound;
          resolve(silentSound);
        },
        onend: () => {
          console.log(`Ambient track ended: ${track.name}`);
          // Auto-play next track if not the last one
          if (index < AUDIO_CONFIG.ambientTracks.length - 1) {
            this.playAmbient(index + 1);
          }
        }
      });
    });
  }

  /**
   * Create silent placeholder when audio fails to load
   * @private
   */
  createSilentFallback(type) {
    console.warn(`Creating silent fallback for ${type}`);
    return {
      play: () => {
        console.log('Silent fallback: play');
        return 0; // Return a sound ID for consistency
      },
      stop: () => console.log('Silent fallback: stop'),
      volume: (vol) => {
        if (vol !== undefined) {
          console.log(`Silent fallback: volume set to ${vol}`);
          return this; // For method chaining
        }
        return 1.0; // Default volume
      },
      mute: () => {
        console.log('Silent fallback: mute');
        return this; // For method chaining
      },
      unmute: () => {
        console.log('Silent fallback: unmute');
        return this; // For method chaining
      },
      playing: () => false,
      state: () => 'unloaded',
      on: (event, fn) => {
        console.log(`Silent fallback: added ${event} listener`);
        return this; // For method chaining
      },
      off: (event, fn) => {
        console.log(`Silent fallback: removed ${event} listener`);
        return this; // For method chaining
      },
      once: (event, fn) => {
        console.log(`Silent fallback: added one-time ${event} listener`);
        return this; // For method chaining
      },
      fade: (from, to, duration) => {
        console.log(`Silent fallback: fade from ${from} to ${to} over ${duration}ms`);
        return this; // For method chaining
      },
      // Additional methods for better compatibility
      load: () => Promise.resolve(),
      unload: () => {},
      _duration: 0,
      _sounds: [],
      _src: ['silent-fallback']
    };
  }
  
  /**
   * Switch to the next ambient track
   */
  nextTrack() {
    if (this.ambientTracks.length === 0) return;
    
    // Stop current track if playing
    if (this.sounds.ambient) {
      this.sounds.ambient.stop();
    }
    
    // Move to next track
    this.currentTrackIndex = (this.currentTrackIndex + 1) % this.ambientTracks.length;
    
    // Play the next track
    this.playAmbient();
  }
  
  /**
   * Update sound toggle button UI based on mute state
   */
  updateSoundToggleUI() {
    const toggleBtn = document.querySelector('.sound-toggle');
    if (!toggleBtn) return;
    
    if (this.isMuted) {
      toggleBtn.textContent = '🔇';
      toggleBtn.title = 'Unmute';
      toggleBtn.setAttribute('aria-label', 'Unmute sound');
      toggleBtn.setAttribute('data-state', 'muted');
    } else {
      toggleBtn.textContent = '🔊';
      toggleBtn.title = 'Mute';
      toggleBtn.setAttribute('aria-label', 'Mute sound');
      toggleBtn.setAttribute('data-state', 'unmuted');
    }
  }
  
  /**
   * Start playing ambient audio
   * @returns {boolean} True if playback started successfully
   */
  playAmbient() {
    if (!this.isInitialized) {
      console.warn('Audio system not initialized');
      return false;
    }
    
    if (this.isPlaying) {
      console.log('Ambient audio is already playing');
      return true;
    }
    
    const track = this.ambientTracks[this.currentTrackIndex];
    if (!track) {
      console.error('No ambient track available');
      return false;
    }
    
    try {
      // Ensure audio is unmuted when starting playback
      if (this.isMuted) {
        Howler.mute(false);
        this.isMuted = false;
      }
      
      track.play();
      this.isPlaying = true;
      console.log('Started ambient audio playback');
      return true;
    } catch (error) {
      console.error('Error starting ambient audio:', error);
      return false;
    }
  }
  
  /**
   * Stop ambient audio
   */
  stopAmbient() {
    if (!this.isPlaying || !this.isInitialized) return;
    
    const track = this.ambientTracks[this.currentTrackIndex];
    if (track) {
      try {
        track.stop();
        this.isPlaying = false;
        console.log('Stopped ambient audio');
      } catch (error) {
        console.error('Error stopping ambient audio:', error);
      }
    }
  }
  
  /**
   * Toggle ambient audio playback
   */
  toggleAmbient() {
    if (this.isPlaying) {
      this.stopAmbient();
    } else {
      this.playAmbient();
    }
  }
  
  /**
   * Play a sound effect
   * @param {string} name - Name of the effect to play
   * @returns {number|boolean} Sound ID if played, false otherwise
   */
  playEffect(name) {
    if (!this.isInitialized || this.isMuted) return false;
    
    const effect = this.sounds.effects[name];
    if (!effect) {
      console.warn(`Sound effect '${name}' not found`);
      return false;
    }
    
    try {
      const soundId = effect.play();
      console.log(`Playing effect: ${name}`);
      return soundId;
    } catch (error) {
      console.error(`Error playing effect '${name}':`, error);
      return false;
    }
  }
  
  /**
   * Mute all audio
   */
  mute() {
    if (this.isMuted || !this.isInitialized) return;
    
    this.wasPlayingBeforeMute = this.isPlaying;
    
    try {
      // Mute using Howler for all sounds
      Howler.mute(true);
      
      this.isMuted = true;
      this.updateSoundToggleUI();
      console.log('Audio muted');
      
      return true;
    } catch (error) {
      console.error('Error muting audio:', error);
      return false;
    }
  }
  
  /**
   * Unmute all audio
   */
  unmute() {
    if (!this.isMuted || !this.isInitialized) return;
    
    try {
      // Unmute using Howler for all sounds
      Howler.mute(false);
      
      this.isMuted = false;
      this.updateSoundToggleUI();
      console.log('Audio unmuted');
      
      // Resume playback if it was playing before mute
      if (this.wasPlayingBeforeMute) {
        this.playAmbient();
      }
      
      return true;
    } catch (error) {
      console.error('Error unmuting audio:', error);
      return false;
    }
  }
  
  /**
   * Toggle mute state
   */
  toggleMute() {
    if (this.isMuted) {
      this.unmute();
    } else {
      this.mute();
    }
    
    // Dispatch custom event for UI updates
    document.dispatchEvent(new CustomEvent('audioStateChanged', {
      detail: { isMuted: this.isMuted }
    }));
  }
  
  /**
   * Play a sound by name (alias for playEffect)
   * @param {string} name - Name of the sound to play
   * @returns {number|boolean} Sound ID if played, false otherwise
   */
  playSound(name) {
    if (!this.isInitialized || this.isMuted) return false;
    return this.playEffect(name);
  }
  
  /**
   * Setup audio unlock for mobile devices
   * @private
   */
  setupAudioUnlock() {
    /**
     * This function is called on user interaction to unlock audio
     */
    this.unlock = () => {
      if (this.isUnlocked) return;
      
      // Create empty buffer
      const buffer = this.audioContext.createBuffer(1, 1, 22050);
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);
      
      // Play the empty buffer
      if (source.start) {
        source.start(0);
      } else if (source.noteOn) {
        source.noteOn(0); // Legacy Web Audio API
      }
      
      this.isUnlocked = true;
      console.log('Audio unlocked');
      
      // Clean up
      source.disconnect();
    };
    
    /**
     * Setup audio unlock on user interaction
     * @private
     */
    this._setupAudioUnlock = () => {
      const unlock = this.unlock.bind(this);
      
      // Unlock on any user interaction
      const events = ['touchstart', 'touchend', 'mousedown', 'keydown'];
      const unlockHandler = () => {
        events.forEach(event => {
          document.removeEventListener(event, unlockHandler, true);
        });
        unlock();
      };
      
      events.forEach(event => {
        document.addEventListener(event, unlockHandler, true);
      });
    };
  }
  
  /**
   * Unlock audio context (call this on user interaction)
   */
  unlockAudio() {
    if (!this.audioContext || this.isUnlocked) return;
    
    // iOS requires creating a new AudioContext after user interaction
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().then(() => {
        console.log('AudioContext resumed successfully');
        this.isUnlocked = true;
      }).catch(error => {
        console.error('Error resuming AudioContext:', error);
      });
    }
  }
}

// Create and export a singleton instance
export const audioManager = new AudioManager();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    audioManager.init().catch(error => {
      console.error('Error initializing audio manager:', error);
    });
  });
} else {
  audioManager.init().catch(error => {
    console.error('Error initializing audio manager:', error);
  });
}

// Add keyboard shortcut for toggling audio (M key)
document.addEventListener('keydown', (event) => {
  if (event.key === 'm' || event.key === 'M') {
    audioManager.toggleMute();
    console.log(`Audio muted via keyboard: ${audioManager.isMuted}`);
  }
  
  // N key to switch to next track
  if (event.key === 'n' || event.key === 'N') {
    audioManager.nextTrack();
  }
});